import { makeStyles, mergeClasses, Spinner, tokens } from '@fluentui/react-components';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { useStore } from 'react-redux';

import { AlertType } from '../../libs/models/AlertType';
import { DictationProblem, useDictation } from '../../libs/voice/useDictation';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { AppStore, RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { Mic20Filled, Mic20Regular } from '../shared/BundledIcons';

const useClasses = makeStyles({
    // Left of the voice conversation button (VoiceButton, right: 54px), which sits left of send.
    button: {
        position: 'absolute',
        right: '94px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground2,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:disabled': {
            cursor: 'default',
            color: tokens.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent',
        },
    },
    active: {
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        '&:hover': {
            backgroundColor: tokens.colorPaletteRedForeground1,
        },
    },
    visuallyHidden: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: 0,
        overflow: 'hidden',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: 'none',
    },
});

const problemMessages: Record<DictationProblem, string> = {
    microphone: 'chatInput.voiceMicrophone',
    audio_blocked: 'chatInput.dictationAudioBlocked',
    nothing_heard: 'chatInput.dictationNothingHeard',
    no_words: 'chatInput.dictationNoWords',
    failed: 'chatInput.dictationFailed',
    cut_off: 'chatInput.dictationCutOff',
};

interface DictationButtonProps {
    chatId: string;
    /** While the input takes no text, or a voice conversation holds the microphone. */
    disabled: boolean;
    onText: (text: string) => void;
    onActiveChange: (active: boolean) => void;
}

/** Dictation: what the user says is written into the chat input, and they send it as a typed question. */
export const DictationButton: React.FC<DictationButtonProps> = ({ chatId, disabled, onText, onActiveChange }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const store = useStore() as AppStore;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);

    const { state, level, start, stop } = useDictation({
        chatId,
        getToken: () => store.getState().app.authToken ?? undefined,
        hostContext,
        onText,
        onProblem: (problem) => {
            dispatch(addAlert({ message: t(problemMessages[problem]), type: AlertType.Warning }));
        },
    });

    const active = state !== 'idle';
    useEffect(() => {
        onActiveChange(active);
    }, [active, onActiveChange]);
    // Hidden mid-dictation (an agent takes the chat), the button unmounts while active; without this the input would
    // stay blocked from sending until the chat is switched or the page reloaded.
    useEffect(
        () => () => {
            onActiveChange(false);
        },
        [onActiveChange],
    );

    const recording = state === 'recording';
    // While starting too: a click then calls the start off.
    const title = active ? t('chatInput.dictationStop') : t('chatInput.dictationStart');
    const status =
        state === 'recording'
            ? t('chatInput.dictationListening')
            : state === 'transcribing'
              ? t('chatInput.dictationTranscribing')
              : '';

    return (
        <>
            <button
                type="button"
                title={title}
                aria-label={title}
                data-testid="dictation-button"
                className={mergeClasses(classes.button, recording && classes.active)}
                // A running dictation can always be stopped, so the microphone is never left open behind a disabled button.
                disabled={(disabled && !active) || state === 'transcribing'}
                onClick={() => {
                    if (active) {
                        stop();
                    } else {
                        void start();
                    }
                }}
            >
                {state === 'starting' || state === 'transcribing' ? (
                    <Spinner size="tiny" aria-hidden />
                ) : recording ? (
                    <Mic20Filled
                        style={{ transform: `scale(${(1 + level * 0.25).toFixed(2)})`, transition: 'transform 120ms' }}
                    />
                ) : (
                    <Mic20Regular />
                )}
            </button>
            <span className={classes.visuallyHidden} aria-live="polite" data-testid="dictation-status">
                {status}
            </span>
        </>
    );
};
